import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { ShippingCountryStep } from "@/components/checkout/ShippingCountryStep";
import { CheckoutInvoiceSection, invoiceDraftValid, type InvoiceDraft, type InvoiceCheck } from "@/components/checkout/CheckoutInvoiceSection";
import { useCart, selectSubtotalCents } from "@/lib/cart/cart-store";
import { formatEuro } from "@/hooks/useShippingConfig";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { createCheckoutSession } from "@/lib/checkout/checkout.functions";
import { supabase } from "@/integrations/supabase/client";
import { trackBeginCheckout, trackCheckoutStep, trackCheckoutError } from "@/lib/analytics/tracker";
import type { ShippingZoneDTO } from "@/lib/shipping.functions";
import { COUNTRY_MAP } from "@/lib/countries";
import { ShieldCheck, Truck, RotateCcw, Loader2, ChevronLeft, CheckCircle2 } from "lucide-react";
import { RETURNS_DAYS_DEFAULT } from "@/lib/site-config";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Checkout — FIXHEN" },
      { name: "description", content: "Secure checkout for your FIXHEN order." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CheckoutPage,
});

type Selection = {
  country: string;
  email: string;
  zone: ShippingZoneDTO;
  shippingCents: number;
};

function CheckoutPage() {
  const items = useCart((s) => s.items);
  const subtotal = useCart(selectSubtotalCents);
  const navigate = useNavigate();
  const [authEmail, setAuthEmail] = useState<string | undefined>();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [preview, setPreview] = useState<{ country: string | null; zone: ShippingZoneDTO | null; shippingCents: number | null }>({ country: null, zone: null, shippingCents: null });
  const [error, setError] = useState<string | null>(null);
  const [invoiceOn, setInvoiceOn] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState<InvoiceDraft | null>(null);
  const [invoiceCheck, setInvoiceCheck] = useState<InvoiceCheck>(null);
  const invoice = invoiceOn && invoiceDraft ? invoiceDraft : null;

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setAuthEmail(data.user?.email ?? undefined));
  }, []);

  useEffect(() => {
    if (items.length === 0) navigate({ to: "/cart" });
  }, [items.length, navigate]);

  useEffect(() => {
    if (items.length === 0) return;
    trackBeginCheckout({ items: items.length, subtotalEur: subtotal / 100, method: "standard" });
    // Only once per checkout page mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selection) return;
    trackCheckoutStep("country_selected", {
      country: selection.country,
      shipping_eur: selection.shippingCents / 100,
      zone: selection.zone?.name_en,
    });
  }, [selection]);

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    if (!selection) throw new Error("Shipping country not selected");
    setError(null);
    trackCheckoutStep("payment_requested", { items: items.length, subtotal_eur: subtotal / 100 });
    const returnUrl = `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`;
    const result = await createCheckoutSession({
      data: {
        items: items.map((i) => ({
          productId: i.productId,
          variantId: i.variantId,
          qty: i.qty,
        })),
        email: selection.email,
        shippingCountry: selection.country,
        returnUrl,
        environment: getStripeEnvironment(),
        ...(invoice
          ? {
              invoice: {
                persona: invoice.persona,
                legal_name: invoice.legal_name.trim(),
                vat_number: invoice.vat_number.trim() || null,
                line1: invoice.line1.trim(),
                line2: invoice.line2.trim() || null,
                city: invoice.city.trim(),
                postal_code: invoice.postal_code.trim(),
                country_code: invoice.country_code,
                email: selection.email,
                save: invoice.save,
                billing_profile_id: invoice.billing_profile_id,
              },
            }
          : {}),
      },
    });
    if ("error" in result) {
      setError(result.error);
      trackCheckoutError(result.error, { stage: "create_session" });
      throw new Error(result.error);
    }
    trackCheckoutStep("payment_ready", { subtotal_eur: subtotal / 100 });
    if (result.invoice) {
      setInvoiceCheck((c) => ({ scheme: result.invoice!.scheme, vatValid: result.invoice!.vatValid, viesName: c?.viesName ?? null, viesError: result.invoice!.viesError }));
    }
    return result.clientSecret;
  }, [items, selection, subtotal, invoice]);

  const options = useMemo(
    () => ({
      fetchClientSecret,
      // Fires when Stripe confirms payment inside the embedded form — lets the
      // visit analysis tell "paid" from "left on the payment form".
      onComplete: () => trackCheckoutStep("stripe_completed", { subtotal_eur: subtotal / 100 }),
    }),
    [fetchClientSecret, subtotal],
  );
  const stripePromise = useMemo(() => getStripe(), []);
  const displayShippingCents = selection?.shippingCents ?? preview.shippingCents;
  const displayCountry = selection?.country ?? preview.country;
  
  const reverseCharge = !!invoice && invoiceCheck?.scheme === "eu_b2b_reverse_charge";
  const netSubtotal = reverseCharge ? items.reduce((s2, it) => s2 + Math.round((it.unitPriceCents * 5) / 6) * it.qty, 0) : subtotal;
  const total = netSubtotal + (displayShippingCents ?? 0);

  return (
    <div>
      <AnnouncementBar />
      <SiteHeader />
      <PaymentTestModeBanner />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-extrabold text-[#172554] mb-6">Secure checkout</h1>

        {error && (
          <div role="alert" data-form-error className="mb-4 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-[1fr_360px]">
          <div className="rounded-2xl border bg-white overflow-hidden min-h-[600px]">
            {items.length === 0 ? (
              <div className="flex items-center justify-center py-24 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : !selection ? (
              <>
                <ShippingCountryStep
                  subtotalCents={subtotal}
                  initialEmail={authEmail}
                  onContinue={(payload) => {
                    if (invoice) {
                      const problem = invoiceDraftValid(invoice);
                      if (problem) {
                        trackCheckoutError(problem, { stage: "invoice_validation" });
                        setError(problem);
                        return;
                      }
                    }
                    setError(null);
                    setSelection(payload);
                  }}
                  onPreview={setPreview}
                />
                <CheckoutInvoiceSection
                  enabled={invoiceOn}
                  onEnabledChange={setInvoiceOn}
                  draft={invoiceDraft}
                  onDraftChange={setInvoiceDraft}
                  check={invoiceCheck}
                  onCheck={setInvoiceCheck}
                  shippingCountry={preview.country}
                  subtotalCents={subtotal}
                />
              </>
            ) : (
              <div>
                <div className="flex items-center justify-between border-b bg-slate-50/60 px-6 py-3">
                  <button
                    onClick={() => {
                      trackCheckoutStep("change_country", { from: selection.country });
                      setSelection(null);
                    }}
                    className="flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-[#172554]"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Change country
                  </button>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    <span>
                      Shipping to{" "}
                      <span className="font-bold text-[#172554]">
                        {COUNTRY_MAP[selection.country]?.flag} {COUNTRY_MAP[selection.country]?.name}
                      </span>
                      {" · "}
                      <span className="font-bold text-[#172554] tabular-nums">
                        {selection.shippingCents === 0 ? "Free" : formatEuro(selection.shippingCents)}
                      </span>
                    </span>
                  </div>
                </div>
                {/* key={selection.country} forces a fresh EmbeddedCheckoutProvider
                    when the buyer changes country — Stripe forbids swapping the
                    client_secret on an existing provider. */}
                <EmbeddedCheckoutProvider
                  key={`${selection.country}-${selection.email}-${invoice ? JSON.stringify(invoice) : "noinv"}`}
                  stripe={stripePromise}
                  options={options}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}
          </div>

          <aside className="space-y-3">
            <div className="rounded-2xl border bg-white p-5">
              <h2 className="text-sm font-bold text-[#172554]">Order summary</h2>
              <ul className="mt-3 divide-y">
                {items.map((it) => (
                  <li key={it.key} className="flex gap-3 py-2">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded border bg-white">
                      {it.image ? <img src={it.image} alt="" className="h-full w-full object-contain p-0.5" /> : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="line-clamp-1 text-xs font-semibold text-[#172554]">{it.title}</p>
                      <p className="text-[10px] text-slate-500">
                        <span className="font-mono">{it.sku}</span>
                        {it.variantLabel ? <> · {it.variantLabel}</> : null}
                        {" · "}Qty {it.qty}
                      </p>
                    </div>
                    <div className="text-xs font-semibold tabular-nums text-[#172554]">
                      {formatEuro(it.unitPriceCents * it.qty)}
                    </div>
                  </li>
                ))}
              </ul>
              <dl className="mt-3 space-y-1 border-t pt-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-600">Subtotal</dt>
                  <dd className="font-semibold tabular-nums">
                    {reverseCharge ? (
                      <>
                        <span className="mr-1.5 text-xs font-normal text-slate-400 line-through">{formatEuro(subtotal)}</span>
                        {formatEuro(netSubtotal)}
                      </>
                    ) : formatEuro(subtotal)}
                  </dd>
                </div>
                {reverseCharge ? (
                  <div className="flex justify-between text-xs">
                    <dt className="text-emerald-700" title="EU B2B reverse charge — VAT accounted for by the buyer (Art. 138, Directive 2006/112/EC)">VAT removed (reverse charge) ⓘ</dt>
                    <dd className="rounded-full bg-emerald-100 px-2 font-semibold text-emerald-700 tabular-nums">−{formatEuro(subtotal - netSubtotal)}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between text-sm">
                  <dt className="text-slate-600">
                    Shipping
                    {displayCountry && (
                      <span className="ml-1 text-[10px] font-normal text-slate-400">
                        · {COUNTRY_MAP[displayCountry]?.flag} {COUNTRY_MAP[displayCountry]?.name ?? displayCountry}
                      </span>
                    )}
                  </dt>
                  <dd className={`font-semibold tabular-nums ${displayShippingCents === 0 ? "text-emerald-600" : ""}`}>
                    {displayShippingCents !== null && displayShippingCents !== undefined
                      ? displayShippingCents === 0
                        ? "Free"
                        : formatEuro(displayShippingCents)
                      : <span className="text-xs font-normal text-slate-500">Select country →</span>}
                  </dd>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2 text-base">
                  <dt className="font-bold text-[#172554]">Total</dt>
                  <dd className="font-extrabold tabular-nums text-[#172554]">
                    {formatEuro(total)}
                  </dd>
                </div>
              </dl>
              {invoice ? (
                <p className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-[11px] text-sky-800">
                  🧾 Invoice requested{invoice.legal_name ? ` for ${invoice.legal_name}` : ""} — it will be emailed to you shortly after your order.
                </p>
              ) : null}
              <Link to={"/cart" as never} className="mt-3 block text-center text-xs text-royal hover:underline">
                ← Back to cart
              </Link>
            </div>
            <div className="rounded-2xl border bg-white p-5 text-xs text-slate-600 space-y-2">
              <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-royal" /> SSL-encrypted payment via Stripe</div>
              <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-royal" /> Ships worldwide, free over €99</div>
              <div className="flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5 text-royal" /> {RETURNS_DAYS_DEFAULT}-day returns</div>
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
